import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import theme from "../../../assets/theme";
import {
  Modal,
  FormControlLabel,
  Radio,
  RadioGroup,
  Grid,
  makeStyles,
} from "@material-ui/core";
import { Formik } from "formik";
import useLoader from "../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import {
  downloadWithDifferenFileName,
  getErrorMessage,
} from "../../../helpers/functions";
import TranscriberService from "../../../services/TranscriberService";
import FormField from "../../common/FormField/FormField";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0)    scale(1);    }
`;

const slideInRight = keyframes`
  from { opacity: 0; transform: translateX(18px); }
  to   { opacity: 1; transform: translateX(0);    }
`;

const slideInLeft = keyframes`
  from { opacity: 0; transform: translateX(-18px); }
  to   { opacity: 1; transform: translateX(0);     }
`;

const glowPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(0, 131, 140, 0.35); }
  50%       { box-shadow: 0 0 0 6px rgba(0, 131, 140, 0);  }
`;

const useStyles = makeStyles(() => ({
  formLabel: {
    fontFamily: "openSans-SemiBold, sans-serif",
    color: "#293461",
  },
}));

const Index = ({ modal, setModal }) => {
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();
  const [inputs, setInputs] = useState([{ type: "text", name: "", label: "" }]);
  const transcriptId = modal?.id;
  const [next, setNext] = useState(false);
  const [isTemplate, setIsTemplate] = useState("india");
  const classes = useStyles();

  useEffect(() => {
    async function getSpeakerNames(id) {
      try {
        const response = await TranscriberService.getSpeakerNames(id);
        if (response?.speakerNames) {
          setInputs(
            response?.speakerNames.map((el) => ({
              type: "text",
              name: el,
              label: el,
            })),
          );
        }
      } catch (error) {
        const message = getErrorMessage(error);
        enqueueSnackbar(message, { variant: "error" });
      }
    }
    if (modal?.state) {
      getSpeakerNames(modal?.id);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal?.state]);

  const handleDownload = async (downloadType, s3Url) => {
    try {
      const filename = s3Url.split("/").pop() || "file.docx";
      if (downloadType === "withLexitas") {
        const response = await fetch(
          `https://staging-api.justact.co.in/lexitas-style-guide`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ fileUrl: s3Url }),
          },
        );
        if (!response.ok) throw new Error("Failed to update Excel file");
        const data = await response.json();
        const processedFileUrl = data?.processed_file;
        if (processedFileUrl) {
          const filename =
            processedFileUrl.split("/").pop() || "processed.docx";
          downloadWithDifferenFileName(
            processedFileUrl,
            decodeURIComponent(filename),
          );
        }
      } else {
        downloadWithDifferenFileName(s3Url, decodeURIComponent(filename));
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setModal({ state: false });
      setNext({ state: false });
      setIsTemplate("");
    }
  };

  async function onFormSubmit(values) {
    try {
      setLoader({ state: true, message: "Post processing transcript..." });
      const payload = {
        replaceSpeaker: values,
        isUkTemplate: next?.isUkTemplate ? next?.isUkTemplate : false,
      };
      const response = await TranscriberService.postProcessingTranscripts(
        transcriptId,
        payload,
      );
      if (response) {
        enqueueSnackbar(response.message, { variant: "success" });
      }
      handleDownload(values.downloadType, response?.fileUrl);
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setLoader({ state: false });
    }
  }

  const radio_inputs = [
    { id: 1, label: "India", value: "india", color: "#00aeef" },
    {
      id: 2,
      label: "Uk",
      value: "uk",
      color: "#00aeef",
      labelColor: "#454545",
    },
    {
      id: 3,
      label: "USA",
      value: "usa",
      color: "#00aeef",
      labelColor: "#454545",
    },
  ];

  return (
    <>
      <Modal
        open={modal?.state}
        onClose={() => {
          setModal(false);
          setIsTemplate("");
          setNext({ state: false });
        }}
      >
        <ModalBackdrop>
          <DrawerContainer>
            <HeadingContainer>
              <StepPill>{next?.state ? "Step 2 of 2" : "Step 1 of 2"}</StepPill>
              <Heading>
                {next?.state ? "Post Processing" : "Choose Document Template"}
              </Heading>
              <CloseButton
                onClick={() => {
                  setModal(false);
                  setIsTemplate("");
                  setNext({ state: false });
                }}
                aria-label="Close modal"
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M1 1l10 10M11 1L1 11"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </CloseButton>
            </HeadingContainer>

            <Divider />

            {next?.state ? (
              <FormContainer $slide="right">
                <Formik
                  initialValues={{ downloadType: "withLexitas" }}
                  enableReinitialize
                  validateOnBlur
                  validateOnChange
                  onSubmit={onFormSubmit}
                >
                  {({
                    values,
                    handleChange,
                    errors,
                    handleSubmit,
                    touched,
                    setFieldValue,
                    setFieldError,
                    handleBlur,
                  }) => (
                    <StyledForm onSubmit={handleSubmit}>
                      <MainContainer>
                        {inputs.map((input, index) => (
                          <HalfWidth key={index}>
                            <FormField
                              {...{
                                input,
                                values,
                                handleChange,
                                errors,
                                handleSubmit,
                                touched,
                                setFieldValue,
                                setFieldError,
                                handleBlur,
                              }}
                            />
                          </HalfWidth>
                        ))}
                      </MainContainer>

                      <Grid
                        item
                        xs={12}
                        spacing={2}
                        style={{ padding: 16, marginLeft: 15 }}
                      >
                        {next?.isUSA && (
                          <>
                            <RadioGroupLabel>Download Format</RadioGroupLabel>
                            <RadioGroup
                              style={{ flexDirection: "row" }}
                              name="downloadType"
                              value={values.downloadType}
                              onChange={(e) =>
                                setFieldValue("downloadType", e.target.value)
                              }
                            >
                              {[
                                { label: "With ATS", value: "withLexitas" },
                                {
                                  label: "Without ATS",
                                  value: "withOutLexitas",
                                },
                              ].map((radio, key) => (
                                <FormControlLabel
                                  {...{ key }}
                                  classes={{ label: classes.formLabel }}
                                  value={radio.value}
                                  control={
                                    <Radio
                                      size="medium"
                                      style={{ color: "#00838c" }}
                                    />
                                  }
                                  label={radio.label}
                                />
                              ))}
                            </RadioGroup>
                          </>
                        )}
                      </Grid>

                      <ButtonContainer>
                        <ModernOutlineButton
                          onClick={() => setNext({ state: false })}
                          type="button"
                        >
                          Back
                        </ModernOutlineButton>
                        <ModernPrimaryButton
                          disabled={inputs[0]?.name?.length > 0 ? false : true}
                          onClick={handleSubmit}
                          type="button"
                        >
                          Submit
                        </ModernPrimaryButton>
                      </ButtonContainer>
                    </StyledForm>
                  )}
                </Formik>
              </FormContainer>
            ) : (
              <FormContainer $slide="left">
                <TemplateGrid>
                  {radio_inputs.map((item) => (
                    <TemplateCard
                      key={item.id + item.value}
                      $active={isTemplate === item.value}
                      onClick={() => setIsTemplate(item.value)}
                      type="button"
                    >
                      {isTemplate === item.value && <ActiveIndicator />}
                      <TemplateLabel $active={isTemplate === item.value}>
                        {item.label}
                      </TemplateLabel>
                      <HiddenRadio
                        type="radio"
                        name="template"
                        value={item.value}
                        checked={isTemplate === item.value}
                        onChange={() => setIsTemplate(item.value)}
                      />
                    </TemplateCard>
                  ))}
                </TemplateGrid>

                <ButtonContainer>
                  <ModernCloseButton
                    onClick={() => {
                      setModal(false);
                      setIsTemplate("");
                    }}
                    type="button"
                  >
                    Cancel
                  </ModernCloseButton>
                  <ModernPrimaryButton
                    onClick={() => {
                      if (isTemplate === "") {
                        enqueueSnackbar("Please choose document template", {
                          variant: "warning",
                        });
                      } else {
                        setNext({
                          state: true,
                          isUkTemplate: isTemplate === "uk",
                          isUSA: isTemplate === "usa",
                        });
                      }
                    }}
                    type="button"
                  >
                    Next
                  </ModernPrimaryButton>
                </ButtonContainer>
              </FormContainer>
            )}
          </DrawerContainer>
        </ModalBackdrop>
      </Modal>
    </>
  );
};

export default Index;

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const DrawerContainer = styled.div`
  max-width: 500px;
  width: 100%;
  background: #ffffff;
  outline: none;
  border-radius: 24px;
  overflow: hidden;
  box-shadow:
    0 24px 64px rgba(41, 52, 97, 0.18),
    0 4px 16px rgba(41, 52, 97, 0.1),
    0 0 0 1px rgba(41, 52, 97, 0.06);
  animation: ${fadeUp} 0.38s cubic-bezier(0.16, 1, 0.3, 1) both;

  @media (max-width: 540px) {
    max-width: 100%;
    border-radius: 20px;
  }
`;

const HeadingContainer = styled.div`
  padding: 24px 24px 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;
`;

const StepPill = styled.span`
  font-family: "DM Sans", sans-serif;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #00838c;
  background: rgba(0, 131, 140, 0.09);
  border: 1px solid rgba(0, 131, 140, 0.22);
  border-radius: 100px;
  padding: 3px 10px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const Heading = styled.h3`
  margin: 0;
  flex: 1;
  font-family: openSans-SemiBold, sans-serif;
  font-size: clamp(16px, 3.5vw, 19px);
  font-weight: 700;
  color: #293461;
  letter-spacing: -0.02em;
  text-align: center;
`;

const CloseButton = styled.button`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1.5px solid #e8ecf5;
  background: #f4f6fb;
  color: #8898b8;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: #fee8ed;
    border-color: #f5a0b0;
    color: #d0384f;
    transform: rotate(90deg);
  }
`;

const Divider = styled.div`
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    #e8ecf5 20%,
    #e8ecf5 80%,
    transparent 100%
  );
  margin: 0 24px;
`;

const FormContainer = styled.div`
  padding: 24px;
  animation: ${({ $slide }) =>
      $slide === "right" ? slideInRight : slideInLeft}
    0.3s ease both;

  @media (max-width: 400px) {
    padding: 16px;
  }
`;

const TemplateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 8px;

  @media (max-width: 360px) {
    grid-template-columns: 1fr;
  }
`;

const TemplateCard = styled.button`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  font-family: openSans-SemiBold, sans-serif;
  padding: 20px 12px 16px;
  border-radius: 16px;
  border: 2px solid ${({ $active }) => ($active ? "#00838c" : "#e8ecf5")};
  background: ${({ $active }) =>
    $active
      ? "linear-gradient(145deg, rgba(0,131,140,0.07) 0%, rgba(0,174,239,0.05) 100%)"
      : "#fafbfe"};
  cursor: pointer;
  transition: all 0.22s cubic-bezier(0.16, 1, 0.3, 1);
  box-shadow: ${({ $active }) =>
    $active
      ? "0 4px 16px rgba(0,131,140,0.16), 0 0 0 1px rgba(0,131,140,0.1)"
      : "0 1px 4px rgba(41,52,97,0.06)"};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ $active }) =>
      $active
        ? "0 8px 24px rgba(0,131,140,0.2)"
        : "0 4px 14px rgba(41,52,97,0.10)"};
  }

  &:active {
    transform: translateY(0);
  }
`;

const ActiveIndicator = styled.span`
  position: absolute;
  top: 9px;
  right: 9px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #00838c;
  box-shadow: 0 0 0 2px rgba(0, 131, 140, 0.2);
  animation: ${glowPulse} 2s infinite;
`;

const TemplateLabel = styled.span`
  font-family: "Outfit", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${({ $active }) => ($active ? "#293461" : "#8898b8")};
  transition: color 0.2s;
`;

const HiddenRadio = styled.input`
  position: absolute;
  opacity: 0;
  pointer-events: none;
  width: 0;
  height: 0;
`;

const MainContainer = styled.div`
  max-height: 300px;
  overflow-y: auto;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: #f0f2f8;
    border-radius: 2px;
  }
  &::-webkit-scrollbar-thumb {
    background: #c8d0e8;
    border-radius: 2px;
  }
`;

const HalfWidth = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 8px;

  @media (max-width: 400px) {
    padding: 0;
  }
`;

const RadioGroupLabel = styled.p`
  font-family: "DM Sans", sans-serif;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #8898b8;
  margin: 0 0 10px;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #f0f2f8;

  @media (max-width: 360px) {
    flex-direction: column;
  }
`;

const BaseButton = styled.button`
  flex: 1;
  padding: 12px 20px;
  border-radius: 12px;
  font-family: "Outfit", sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.22s cubic-bezier(0.16, 1, 0.3, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  letter-spacing: 0.01em;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    transform: none !important;
    box-shadow: none !important;
  }
`;

const ModernPrimaryButton = styled(BaseButton)`
  background: #00838c;
  border: none;
  font-family: openSans-SemiBold, sans-serif;
  color: #ffffff;
`;

const ModernOutlineButton = styled(BaseButton)`
  background: #f4f6fb;
  border: 1.5px solid #e2e8f5;
  font-family: openSans-SemiBold, sans-serif;
  color: #5a6a9a;

  &:hover:not(:disabled) {
    background: #eaeef8;
    border-color: #c8d0e8;
    color: #293461;
    transform: translateY(-1px);
  }
`;

const ModernCloseButton = styled(BaseButton)`
  background: #d91b1b;
  border: 1.5px solid #d91b1b;
  font-family: openSans-SemiBold, sans-serif;
  color: #ffffff;
`;

const StyledForm = styled.form`
  width: 100%;
`;

export const RadioGrid = styled.div`
  margin-left: 45px;
  @media ${theme?.breakpoints?.sm_up} {
    display: grid;
    grid-templates: 1fr 1fr;
  }
`;
